import type { PageInfo } from '../../../model';
import type { ApiCallerResult } from './ApiCallerResult';

export interface Query {
  filter?: string;
  limit?: number;
  cursor?: string;
  orderBy?: string;
  [key: string]: string | number | any | undefined;
}

// #TODO: find a way to make path and query type-safe
export interface ApiCallerOptions {
  path?: { [key: string]: string[] | string | number | undefined | null };
  query?: Query;
}

export interface Paginateable {
  pageInfo: PageInfo;
}

export type ApiCallerKeyFn = (options: ApiCallerOptions) => string;
export type ApiCallerCallFn<T> = (options: ApiCallerOptions) => Promise<T>;

/**
 * A read endpoint as a value: a cache key paired with the call that key stands for.
 *
 * The pairing is the reason this type exists, and it does not reduce to a bare
 * `() => repo.list(options)` thunk. SWR has to know the key *before* it decides
 * whether to call at all — that is what deduplicates concurrent readers, answers
 * from cache, and tells `mutate` what to invalidate. A thunk can only report a key
 * by running, which is too late. Keeping both halves in one object also guarantees
 * they are derived from the same options, so the key cannot describe a request
 * different from the one that gets made.
 *
 * It has to be one value rather than two loose functions because callers travel as
 * data: `EntityList`/`EntitySelect` take one as a `loader` prop, the filter configs
 * hold `loader`/`lookup` pairs, `useGetBatch` takes an array, and four hooks
 * (`useGet`, `useGetInfinite`, `useGetBatch`, `useGetAllPages`) consume the same
 * caller. Inlining a caller into whichever hook happens to use it today breaks the
 * others and the components that only ever receive one as a prop.
 *
 * It also keeps `libs/core` free of any runtime React or SWR dependency while still
 * supplying what SWR needs (`Response.ts` imports an SWR type only).
 *
 * Instance options are merged *under* the ones the call site passes, so a caller can
 * be pre-bound to a fixed path (`new GetAdvertiserApiCaller({ path: { id } })`) and
 * still take per-call overrides.
 */
export default class ApiCaller<T = any> {
  /**
   * Whether `call` resolves to a `{ data, pageInfo }` envelope rather than the value
   * itself. Readers need this to know what to unwrap; sniffing the response for a
   * `data` key mis-reads any entity that happens to have one.
   */
  readonly returnsEnvelope: boolean = false;

  constructor(
    private keyFn: ApiCallerKeyFn,
    private callFn: ApiCallerCallFn<T>,
    protected options?: ApiCallerOptions,
  ) {}

  getKey(options: ApiCallerOptions) {
    return this.keyFn(this.buildOptions(options));
  }

  getKeyInfinite<Data extends Paginateable>(
    pageIndex: number,
    previousPageData: Data | null,
    options: ApiCallerOptions,
  ) {
    if (previousPageData && !previousPageData.pageInfo.nextCursor) return null;

    const callOptions = this.buildOptions(options);

    if (pageIndex === 0) return { url: this.keyFn(callOptions), ...callOptions };

    const nextPageOptions = {
      url: this.keyFn(callOptions),
      query: {
        ...callOptions.query,
        cursor: previousPageData?.pageInfo.nextCursor,
      },
      path: callOptions.path,
    };
    return nextPageOptions;
  }

  call(options: ApiCallerOptions): Promise<T> {
    return this.callFn(this.buildOptions(options));
  }

  buildOptions(options: ApiCallerOptions) {
    return {
      path: { ...this.options?.path, ...options.path },
      query: { ...this.options?.query, ...options.query },
    };
  }
}

/**
 * Builds a caller type for one endpoint from its two halves. The result is `new`-able,
 * so a call site can bind per-instance defaults: `new ListCampaignsApiCaller({ path })`.
 *
 * The key function is the repo's `static *CallKey`; it must produce the URL the call
 * will actually request, since that string is the SWR cache key across every hook and
 * component that reads the endpoint.
 */
export function defineApiCaller<T>(keyFn: ApiCallerKeyFn, callFn: ApiCallerCallFn<T>) {
  return class extends ApiCaller<T> {
    constructor(options?: ApiCallerOptions) {
      super(keyFn, callFn, options);
    }
  };
}

/**
 * A caller whose endpoint answers with the `{ data, pageInfo }` envelope. Distinct from
 * `ApiCaller` at the type level so a reader can be typed on the unwrapped `D` and so
 * the two shapes cannot be confused for one another.
 */
export class EnvelopeApiCaller<D> extends ApiCaller<ApiCallerResult<D>> {
  override readonly returnsEnvelope = true as const;
}

/** As `defineApiCaller`, for an endpoint that answers with the envelope. */
export function defineEnvelopeApiCaller<D>(
  keyFn: ApiCallerKeyFn,
  callFn: ApiCallerCallFn<ApiCallerResult<D>>,
) {
  return class extends EnvelopeApiCaller<D> {
    constructor(options?: ApiCallerOptions) {
      super(keyFn, callFn, options);
    }
  };
}
